import _ from 'lodash'
import { useTranslation } from 'react-i18next'

import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import PersonIcon from '@mui/icons-material/Person'

import { Consultation, ConsultationStatus } from '@/core/models/consultation.model'

type GroupPatientsBarProps = {
  consultations: Consultation[]
  activeConsultationId?: string
  onSelectConsultation: (consultationId: string) => void
}

function getStatusColor(status: ConsultationStatus): 'success' | 'warning' | 'error' | 'info' | 'default' {
  switch (status) {
    case ConsultationStatus.Active:
      return 'success'
    case ConsultationStatus.Completed:
      return 'default'
    case ConsultationStatus.Scheduled:
      return 'info'
    case ConsultationStatus.Canceled:
      return 'error'
    default:
      return 'default'
  }
}

export function GroupPatientsBar(props: GroupPatientsBarProps) {
  const { consultations, activeConsultationId, onSelectConsultation } = props
  const { t } = useTranslation()

  if (!consultations.length) return null

  return (
    <Box
      sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        px: 1.5,
        py: 1,
        overflowX: 'auto',
        flexShrink: 0,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        {consultations.map((consultation) => {
          const patient = consultation.patient
          const patientName = patient
            ? _.trim(`${patient.userInfo?.name || ''} ${patient.userInfo?.surname || ''}`)
            : '-'
          const isActive = consultation._id === activeConsultationId
          const statusColor = getStatusColor(consultation.status)

          return (
            <Tooltip
              key={consultation._id}
              title={t(`common:model.consultation.status.${consultation.status}.label`, { defaultValue: consultation.status })}
            >
              <Chip
                avatar={
                  <Avatar sx={{ bgcolor: isActive ? 'primary.main' : 'grey.400' }}>
                    <PersonIcon sx={{ fontSize: 16, color: 'white' }} />
                  </Avatar>
                }
                label={patientName}
                onClick={() => onSelectConsultation(consultation._id)}
                variant={isActive ? 'filled' : 'outlined'}
                color={isActive ? 'primary' : statusColor}
                sx={{
                  fontWeight: isActive ? 600 : 400,
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
          )
        })}
      </Stack>
    </Box>
  )
}

export default GroupPatientsBar
